import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";
import { eur } from "./docUtils";
import QuickClientDialog from "./QuickClientDialog";

const num = (v) => parseFloat(String(v).replace(",", ".")) || 0;
const emptyItem = () => ({ prestation: "", description: "", qty: 1, price_ht: "" });
const sel = "w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm bg-white";

export default function InvoiceModal({ open, onClose, onSaved, clients, invoice }) {
  const [clientId, setClientId] = useState("");
  const [title, setTitle] = useState("");
  const [items, setItems] = useState([emptyItem()]);
  const [tva, setTva] = useState("non_assujetti");
  const [comment, setComment] = useState("");
  const [discount, setDiscount] = useState("");
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [extraClients, setExtraClients] = useState([]);
  const allClients = [...clients, ...extraClients];

  useEffect(() => {
    if (open) api.get("/accounting/services").then((r) => setServices(r.data)).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setClientId(invoice?.client_id || "");
    setTitle(invoice?.title || "Community management - {{mois_en_cours}}");
    setItems(invoice?.items?.length ? invoice.items.map((i) => ({ ...i })) : [emptyItem()]);
    setTva(invoice?.tva_mode || "non_assujetti");
    setComment(invoice?.comment || "");
    setDiscount(invoice?.discount_percent || "");
  }, [open, invoice]);

  const setItem = (i, k, v) => setItems((its) => its.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  const totalBrut = items.reduce((s, it) => s + num(it.qty || 1) * num(it.price_ht), 0);
  const discountPct = Math.min(100, Math.max(0, num(discount)));
  const discountAmt = Math.round(totalBrut * discountPct) / 100;
  const totalHt = totalBrut - discountAmt;
  const totalTva = tva === "non_assujetti" ? 0 : totalHt * 0.2;
  const totalTtc = totalHt + totalTva;

  const submit = async () => {
    if (!clientId) { toast.error("Sélectionnez un client"); return; }
    const clean = items.filter((it) => it.prestation.trim());
    if (!clean.length) { toast.error("Ajoutez au moins une prestation"); return; }
    setLoading(true);
    try {
      const payload = {
        client_id: clientId, title,
        items: clean.map((it) => ({ ...it, qty: num(it.qty || 1), price_ht: num(it.price_ht) })),
        tva_mode: tva, tva_rate: tva === "non_assujetti" ? 0 : 20, comment,
        discount_percent: discountPct,
      };
      if (invoice) await api.patch(`/accounting/invoices/${invoice.id}`, payload);
      else await api.post("/accounting/invoices", payload);
      toast.success(invoice ? "Facture mise à jour" : "Brouillon de facture créé");
      onSaved();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur lors de l'enregistrement"); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="invoice-modal" aria-describedby={undefined}>
        <DialogTitle className="font-heading font-bold text-lg">
          {invoice ? "Modifier le brouillon" : "Nouvelle facture"}
        </DialogTitle>
        <div className="space-y-4 mt-2">
          <div>
            <Label className="text-xs">Client</Label>
            <select className={`${sel} mt-1`} value={clientId}
              onChange={(e) => e.target.value === "__new__" ? setQuickOpen(true) : setClientId(e.target.value)}
              data-testid="invoice-client">
              <option value="">Sélectionner…</option>
              <option value="__new__" data-testid="invoice-client-new">+ Nouveau client</option>
              <optgroup label="Clients récurrents">
                {allClients.filter((c) => c.kind !== "ponctuel").map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </optgroup>
              {allClients.some((c) => c.kind === "ponctuel") && (
                <optgroup label="Clients non récurrents">
                  {allClients.filter((c) => c.kind === "ponctuel").map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </optgroup>
              )}
            </select>
          </div>

          <div>
            <Label className="text-xs">Titre de la facture (optionnel)</Label>
            <Input className="mt-1" value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex : Community Management - Août 2026" data-testid="invoice-title" />
          </div>

          {services.length > 0 && (
            <div>
              <Label className="text-xs">Depuis la bibliothèque de prestations</Label>
              <select className={`${sel} mt-1`} value="" data-testid="invoice-service-picker"
                onChange={(e) => {
                  const s = services.find((x) => x.id === e.target.value);
                  if (!s) return;
                  const row = { prestation: s.name + (s.unit === "mois" ? " (mensuel)" : ""), description: s.description || "", qty: 1, price_ht: s.price_ht };
                  setItems((its) => (its.length === 1 && !its[0].prestation ? [row] : [...its, row]));
                }}>
                <option value="">Choisir une prestation enregistrée…</option>
                {services.map((s) => <option key={s.id} value={s.id}>{s.name} — {eur(s.price_ht)}{s.unit === "mois" ? " / mois" : ""}</option>)}
              </select>
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-xs">Prestations</Label>
            {items.map((it, i) => (
              <div key={i} className="border border-neutral-200 rounded-lg p-3 space-y-2" data-testid={`invoice-item-${i}`}>
                <div className="flex gap-2">
                  <Input placeholder="Prestation (ex : Community Management)" value={it.prestation}
                    onChange={(e) => setItem(i, "prestation", e.target.value)} className="flex-1" data-testid={`invoice-prestation-${i}`} />
                  {items.length > 1 && (
                    <button onClick={() => setItems((its) => its.filter((_, j) => j !== i))}
                      className="text-neutral-300 hover:text-red-500" data-testid={`invoice-item-remove-${i}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <Textarea placeholder={"Description (optionnel) — retours à la ligne acceptés"} value={it.description}
                  rows={2} className="text-sm"
                  onChange={(e) => setItem(i, "description", e.target.value)} data-testid={`invoice-description-${i}`} />
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-xs">Quantité</Label>
                    <Input type="number" min="0.5" step="0.5" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} className="mt-1" data-testid={`invoice-qty-${i}`} /></div>
                  <div><Label className="text-xs">Prix unitaire HT (€)</Label>
                    <Input type="number" min="0" value={it.price_ht} onChange={(e) => setItem(i, "price_ht", e.target.value)} className="mt-1" data-testid={`invoice-price-${i}`} /></div>
                </div>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setItems((its) => [...its, emptyItem()])} className="gap-1.5" data-testid="invoice-add-item">
              <Plus className="w-4 h-4" /> Ajouter une prestation
            </Button>
          </div>

          <div>
            <Label className="text-xs">TVA</Label>
            <select className={`${sel} mt-1`} value={tva} onChange={(e) => setTva(e.target.value)} data-testid="invoice-tva">
              <option value="non_assujetti">Non assujetti (art. 293 B du CGI)</option>
              <option value="tva">TVA 20 %</option>
            </select>
          </div>

          <div className="border border-neutral-200 rounded-lg p-3 space-y-2" data-testid="invoice-discount-block">
            <Label className="text-xs">Remise (%)</Label>
            <Input type="number" min="0" max="100" step="0.5" value={discount} onChange={(e) => setDiscount(e.target.value)}
              placeholder="0" className="mt-1" data-testid="invoice-discount" />
            {discountPct > 0 && (
              <div className="text-xs bg-neutral-50 rounded-lg p-2.5 space-y-1" data-testid="invoice-discount-preview">
                <div className="flex justify-between"><span className="text-neutral-500">Montant initial</span><span>{eur(totalBrut)}</span></div>
                <div className="flex justify-between"><span className="text-neutral-500">Remise ({discountPct} %)</span><span className="text-red-600">− {eur(discountAmt)}</span></div>
                <div className="flex justify-between font-medium"><span>Total après remise</span><span>{eur(totalHt)}</span></div>
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs">Commentaire (optionnel)</Label>
            <Textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} className="mt-1" data-testid="invoice-comment" />
          </div>

          <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-3 text-sm space-y-1" data-testid="invoice-total">
            {discountPct > 0 && <div className="flex justify-between"><span className="text-neutral-500">Total HT (avant remise)</span><span>{eur(totalBrut)}</span></div>}
            {discountPct > 0 && <div className="flex justify-between"><span className="text-neutral-500">Remise ({discountPct} %)</span><span className="text-red-600">− {eur(discountAmt)}</span></div>}
            <div className="flex justify-between"><span className="text-neutral-500">Total HT</span><span>{eur(totalHt)}</span></div>
            {tva !== "non_assujetti" && <div className="flex justify-between"><span className="text-neutral-500">TVA 20 %</span><span>{eur(totalTva)}</span></div>}
            <div className="flex justify-between font-bold"><span>Total TTC</span><span>{eur(totalTtc)}</span></div>
          </div>

          <Button onClick={submit} disabled={loading} className="w-full bg-neutral-900 hover:bg-neutral-800 text-white" data-testid="invoice-save">
            {loading ? "Enregistrement…" : invoice ? "Enregistrer" : "Créer le brouillon"}
          </Button>
        </div>
      </DialogContent>
      <QuickClientDialog open={quickOpen} onClose={() => setQuickOpen(false)}
        onDone={(c) => { setExtraClients((x) => [...x, c]); setClientId(c.id); setQuickOpen(false); }} />
    </Dialog>
  );
}
